import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { text_colorTextSecondary } from 'ext-ui/style/colors'
import { font_family_weight_regular } from 'ext-ui/style/typography'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AvailableCreditTooltip from 'src/components/AvailableCreditTooltip/AvailableCreditTooltip'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CopyTextLink from 'src/components/CopyTextLink/CopyTextLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgAppleAddToWallet from 'src/components/Svgs/SvgAppleAddToWallet'
import SvgCopy from 'src/components/Svgs/SvgCopy'
import SvgGoogleAddToWallet from 'src/components/Svgs/SvgGoogleAddToWallet'
import SvgGoogleWalletIcon from 'src/components/Svgs/SvgGoogleWalletIcon'
import TestId from 'src/components/TestId/TestId'
import Tooltip from 'src/components/Tooltip/Tooltip'
import VirtualCardDetailImage from 'src/components/VirtualCardDetailImage/VirtualCardDetailImage'
import VirtualCardPaymentInfo from 'src/components/VirtualCardPaymentInfo/VirtualCardPaymentInfo'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  ScreeningStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { updateBillPayUrl, updateVirtualCardUrl } from 'src/navigation/urls'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface VirtualCardDetailHeaderProps {
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  recipient: UserListModel | undefined
  hideCardDetails?: boolean
  hideCardholderName?: boolean
  pendingFinalApproval?: boolean
  showPushToWallet?: boolean
  showAppleAddToWallet?: boolean
  showGoogleAddToWallet?: boolean
  googleWalletAdded?: boolean
  addToWalletButtonId?: string
  onGoogleAddToWalletClick?: () => void
}

const VirtualCardDetailHeader: React.FC<VirtualCardDetailHeaderProps> = ({
  virtualCard,
  virtualCardPermissions,
  recipient,
  hideCardDetails,
  hideCardholderName,
  pendingFinalApproval,
  showPushToWallet,
  showAppleAddToWallet,
  showGoogleAddToWallet,
  googleWalletAdded,
  addToWalletButtonId,
  onGoogleAddToWalletClick,
}): React.ReactElement => {
  const { grayscaleImageEnabled } = useSelector(selectBrandFeaturesFromState)
  const { analytics } = useAnalyticsContext()

  return (
    <div
      css={css`
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
        background-color: ${styleConstants.colors.white};
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 2;
      `}
    >
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: row;
          border-bottom: 1px solid ${styleConstants.colors.gray_light};
          padding: ${styleConstants.heights.headerPaddingLGUp}
            ${styleConstants.widths.headerPaddingXLGUp};

          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            justify-content: center;
            padding: ${styleConstants.heights.headerPaddingMDDown}
              ${styleConstants.widths.headerPaddingXXSMOnly};
          `)};

          ${getBreakpointOnly(breakpointSizes.XSM)(css`
            justify-content: space-between;
            align-items: center;
            padding: ${styleConstants.heights.headerPaddingMDDown}
              ${styleConstants.widths.headerPaddingXSMOnly};
          `)};

          ${getBreakpointOnly(breakpointSizes.SM)(css`
            padding: ${styleConstants.heights.headerPaddingMDDown}
              ${styleConstants.widths.headerPaddingSMOnly};
          `)};

          ${getBreakpointOnly(breakpointSizes.MD)(css`
            padding: ${styleConstants.heights.headerPaddingMDDown}
              ${styleConstants.widths.headerPaddingMDOnly};
          `)};

          ${getBreakpointOnly(breakpointSizes.LG)(css`
            padding: ${styleConstants.heights.headerPaddingMDDown}
              ${styleConstants.widths.headerPaddingLGOnly};
          `)};
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            max-width: 800px;

            ${getBreakpointDown(breakpointSizes.MD)(css`
              max-width: 500px;
            `)};

            ${getBreakpointDown(breakpointSizes.SM)(css`
              max-width: 360px;
            `)};

            ${getBreakpointDown(breakpointSizes.XXSM)(css`
              display: none;
            `)};
          `}
        >
          <span
            data-testid={'virtualCardDetailName'}
            css={css`
              ${getFontCss(
                40,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                ${getFontCss(
                  24,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `)}
            `}
          >
            {virtualCard.truncatedDisplayName}
          </span>
          <span
            css={css`
              margin-top: 10px;
              ${virtualCard.isInactive
                ? getFontCss(
                    40,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.regular,
                  )
                : getFontCss(
                    40,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                ${virtualCard.isInactive
                  ? getFontCss(
                      24,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )
                  : getFontCss(
                      24,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
              `)};
            `}
          >
            {virtualCard.isInactive && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {virtualCard.psuedoStatusLabel}
                {virtualCard.isClosed && (
                  <Tooltip openLeft={false}>
                    <Trns
                      source={
                        'This virtual card has been permanently closed and cannot be reactivated or updated'
                      }
                    />
                  </Tooltip>
                )}
                {!virtualCard.isClosed &&
                  (virtualCardPermissions?.canUpdateVirtualCard ||
                    virtualCardPermissions?.canRequestUpdate) && (
                    <Button
                      linkProps={{
                        to: virtualCard.isBillPay
                          ? updateBillPayUrl(virtualCard.id)
                          : updateVirtualCardUrl(virtualCard.id),
                        ariaLabelSource: 'Update Virtual Card',
                      }}
                      css={css`
                        margin-left: 24px;
                      `}
                      design={ButtonDesigns.designCta}
                      type={ButtonTypes.button}
                      text={
                        virtualCardPermissions?.canUpdateVirtualCard ? (
                          <Trns source={'Update Virtual Card'} />
                        ) : (
                          <Trns source={'Request Update'} />
                        )
                      }
                      onClick={(): void => {
                        virtualCardPermissions?.canRequestUpdate &&
                          analytics?.track(
                            'virtual cards - open request virtual card update flyout - button press',
                            {
                              screen: '/virtualcards',
                              interaction: 'button press',
                              intent: 'open request virtual card update flyout',
                            },
                          )
                      }}
                    />
                  )}
              </div>
            )}
            {!virtualCard.isInactive && (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                `}
              >
                <TestId testId={'virtualCardDetailHeaderAvailableCredit'}>
                  <Crncy
                    currency={virtualCard.currency}
                    amount={virtualCard.balance}
                  />
                </TestId>
                <AvailableCreditTooltip openLeft={false} />
              </div>
            )}
          </span>
          {virtualCard.isConsumed && (
            <div
              css={css`
                margin-top: 10px;
                display: flex;
                flex-direction: column;
              `}
            >
              <span>
                <Trns source={'This card cannot be reactivated.'} />
              </span>
              <span>
                <Trns
                  source={
                    'Need help? Visit our ${resourceCenter} for support and contact options.'
                  }
                  expressions={{
                    resourceCenter: (
                      <ResourceCenterLink size={HoverableLinkSizes.Large} />
                    ),
                  }}
                />
              </span>
            </div>
          )}
        </div>
        <div
          css={css`
            position: absolute;
            right: 120px;
            top: 36px;
            width: 300px;

            ${getBreakpointOnly(breakpointSizes.MD)(css`
              right: 80px;
            `)};

            ${getBreakpointDown(breakpointSizes.MD)(css`
              width: 240px;
            `)};

            ${getBreakpointDown(breakpointSizes.SM)(css`
              top: 80px;
              right: 60px;
            `)};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              position: relative;
              right: auto;
              top: auto;
              border-radius: 3.37%/5.92%;
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            `)};

            ${getBreakpointDown(breakpointSizes.XXSM)(css`
              width: 300px;
            `)};
          `}
        >
          <>
            <VirtualCardDetailImage
              recipient={recipient}
              virtualCard={virtualCard}
              hideCardDetails={hideCardDetails}
              hideCardholderName={hideCardholderName}
              grayscaleImage={grayscaleImageEnabled}
            />
            {(showAppleAddToWallet || showGoogleAddToWallet) && (
              <div
                css={css`
                  margin-top: 20px;
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  gap: 8px;
                `}
              >
                {showAppleAddToWallet && (
                  <button
                    id={addToWalletButtonId}
                    type="button"
                    aria-label="Add to Apple Wallet"
                    css={css`
                      padding: 0;
                      border: none;
                      background: transparent;
                      line-height: 0;
                      cursor: pointer;
                    `}
                  >
                    <SvgAppleAddToWallet
                      css={css`
                        display: block;
                        width: 111px;
                        height: 35px;
                      `}
                    />
                  </button>
                )}
                {showGoogleAddToWallet &&
                  (googleWalletAdded ? (
                    <div
                      css={css`
                        min-height: 40px;
                        padding: 4px 12px;
                        border-radius: 20px;
                        background: ${styleConstants.colors.gray_lightest};
                        display: flex;
                        align-items: center;
                        gap: 8px;
                      `}
                    >
                      <SvgGoogleWalletIcon
                        css={css`
                          width: 18px;
                          height: 18px;
                          display: block;
                          flex-shrink: 0;
                        `}
                      />
                      <span
                        css={css`
                          font-size: 14px;
                          line-height: 18px;
                          color: ${text_colorTextSecondary};
                          font-family: ${font_family_weight_regular};
                          white-space: nowrap;
                        `}
                      >
                        <Trns source={'Added to Google Wallet'} />
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      aria-label="Add to Google Wallet"
                      onClick={onGoogleAddToWalletClick}
                      css={css`
                        padding: 4px 0;
                        border: none;
                        background: transparent;
                        line-height: 0;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                      `}
                    >
                      <SvgGoogleAddToWallet
                        css={css`
                          display: block;
                          height: 40px;
                          width: auto;
                          max-width: 100%;
                        `}
                      />
                    </button>
                  ))}
              </div>
            )}
            {virtualCard.isActive &&
              (virtualCard.screening?.status === undefined ||
                virtualCard.screening.status === ScreeningStatus.Pass ||
                virtualCard.screening.status ===
                  ScreeningStatus.NotApplicable) && (
                <CopyTextLink
                  css={css`
                    margin-top: 8px;
                    ${getBreakpointUp(breakpointSizes.SM)(css`
                      display: none;
                    `)};
                  `}
                  copyValue={virtualCard.activeOrRedactedVcn}
                  copyText={
                    <>
                      <SvgCopy
                        css={css`
                          transform: scale(0.6);
                        `}
                      />
                      <Trns source={'Copy Card Number'} />
                    </>
                  }
                />
              )}
          </>
        </div>
      </div>
      <VirtualCardPaymentInfo
        virtualCard={virtualCard}
        virtualCardPermissions={virtualCardPermissions}
        recipientTruncatedFullNameOrEmail={recipient?.truncatedFullNameOrEmail}
        hideCardDetails={hideCardDetails}
        pendingFinalApproval={pendingFinalApproval}
        showPushToWallet={showPushToWallet}
      />
    </div>
  )
}

export default VirtualCardDetailHeader
